import { Alert, App, type TableProps } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { capturePostHogEvent } from "../../../analytics/posthog";
import { useEtfScreener } from "../../../api/services/etf-screener.hooks";
import { withLocale, splitLocalePath } from "../../../i18n/localePath";
import { PathGenerators } from "../../../router/paths";
import type { EtfRow, EtfSortKey } from "../../../api/services/etf-screener.type";
import Table from "../../components/CustomAntdComponents/Table";
import { CustomPagination } from "../../components/CustomPagination/CustomPagination";
import styles from "../StockScreener/stockScreener.module.css";
import { copyTextToClipboard } from "../StockScreener/screenerUrlState";
import { EtfScreenerFilters } from "./EtfScreenerFilters";
import { EtfScreenerSeoContent } from "./EtfScreenerSeoContent";
import { getEtfColumns, getEtfTableScrollX } from "./etfColumns";
import etfStyles from "./etfScreener.module.css";
import {
  DEFAULT_ETF_SORT,
  EMPTY_ETF_FILTERS,
  ETF_PAGE_SIZE,
  ETF_VIEW_TABS,
  clearEtfSessionState,
  etfFiltersToQuery,
  etfStateToSearchParams,
  hasActiveEtfFilters,
  loadEtfSessionState,
  parseEtfSearchParams,
  saveEtfSessionState,
  type EtfFilterValues,
  type EtfSort,
  type EtfUrlState,
  type EtfViewTab,
} from "./etfFilters";

/**
 * The ETF screener page, server-rendered with the default view's rows (see
 * src/routes/etf-screener.tsx). A shared URL is applied on first render;
 * filters saved in this tab's session are restored after hydration.
 */
export default function EtfScreener() {
  const { t } = useTranslation();
  const p = "pages.etfScreener.";

  return (
    <article className={styles.shell}>
      <div className={styles.inner}>
        <header className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>{t(`${p}title`)}</h1>
          <p className={styles.pageSubtitle}>{t(`${p}subtitle`)}</p>
          <p className={styles.pageIntro}>{t(`${p}seoContent.helpParagraph1`)}</p>
        </header>

        <EtfScreenerPanel />

        <EtfScreenerSeoContent />
      </div>
    </article>
  );
}

function EtfScreenerPanel() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const p = "pages.etfScreener.";

  const [urlState] = useState<EtfUrlState | null>(() =>
    parseEtfSearchParams(new URLSearchParams(search)),
  );
  const [draftFilters, setDraftFilters] = useState<EtfFilterValues>(
    () => urlState?.filters ?? EMPTY_ETF_FILTERS,
  );
  const [appliedFilters, setAppliedFilters] = useState<EtfFilterValues>(
    () => urlState?.filters ?? EMPTY_ETF_FILTERS,
  );
  const [sort, setSort] = useState<EtfSort>(() => urlState?.sort ?? DEFAULT_ETF_SORT);
  const [page, setPage] = useState(urlState?.page ?? 1);
  const [viewTab, setViewTab] = useState<EtfViewTab>("weights");

  // Without a shared URL, pick up where this tab left off. Runs before the save.
  const sessionRestored = useRef(false);
  useEffect(() => {
    if (sessionRestored.current) return;
    sessionRestored.current = true;
    if (urlState) return;
    const session = loadEtfSessionState();
    if (!session) return;
    setDraftFilters(session.draftFilters);
    setAppliedFilters(session.filters);
    setSort(session.sort);
    setPage(session.page);
  }, [urlState]);

  useEffect(() => {
    saveEtfSessionState({ filters: appliedFilters, draftFilters, sort, page });
  }, [appliedFilters, draftFilters, sort, page]);

  const query = useMemo(
    () => etfFiltersToQuery(appliedFilters, sort, page, ETF_PAGE_SIZE),
    [appliedFilters, sort, page],
  );
  const screener = useEtfScreener(query, { placeholderData: (previous) => previous });

  const rows = screener.data?.data ?? [];
  const total = screener.data?.total ?? 0;
  const pageSize = screener.data?.limit ?? ETF_PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));

  // Clamp the page when a filter change shrinks the result set.
  useEffect(() => {
    if (screener.data && page > totalPages) setPage(totalPages);
  }, [page, screener.data, totalPages]);

  const columns = useMemo(
    () => getEtfColumns(t, viewTab, sort, page, ETF_PAGE_SIZE),
    [page, sort, t, viewTab],
  );
  const scrollX = useMemo(() => getEtfTableScrollX(columns), [columns]);

  const handleTableChange: TableProps<EtfRow>["onChange"] = (_pagination, _filters, sorter) => {
    const single = Array.isArray(sorter) ? sorter[0] : sorter;
    const key = single?.columnKey as EtfSortKey | undefined;
    if (!key || !single?.order) {
      setSort(DEFAULT_ETF_SORT);
    } else {
      setSort({ key, order: single.order === "ascend" ? "asc" : "desc" });
    }
    setPage(1);
  };

  // The whole row opens the fund page. The ticker is also a real link
  // (crawlable, middle-click), so clicks on links are left to the link.
  const handleRow = useCallback(
    (row: EtfRow, index: number) => ({
      className: etfStyles.clickableRow,
      onClick: (event: MouseEvent<HTMLElement>) => {
        if ((event.target as HTMLElement).closest("a, button")) return;
        const locale = splitLocalePath(pathname).locale;
        const path = PathGenerators.ETF(row.ticker);
        const href = locale ? withLocale(path, locale) : path;
        capturePostHogEvent("etf_screener_fund_clicked", {
          ticker: row.ticker,
          row_position: (page - 1) * ETF_PAGE_SIZE + index + 1,
        });
        if (event.metaKey || event.ctrlKey) {
          window.open(href, "_blank", "noopener");
          return;
        }
        navigate(href);
      },
    }),
    [navigate, page, pathname],
  );

  const handleApply = useCallback(() => {
    setAppliedFilters(draftFilters);
    setPage(1);
    capturePostHogEvent("etf_screener_filters_applied", {
      has_status: Boolean(draftFilters.status),
      methodology: draftFilters.methodology,
      active: hasActiveEtfFilters(draftFilters),
    });
  }, [draftFilters]);

  const handleClear = useCallback(() => {
    setDraftFilters(EMPTY_ETF_FILTERS);
    setAppliedFilters(EMPTY_ETF_FILTERS);
    setSort(DEFAULT_ETF_SORT);
    setPage(1);
    clearEtfSessionState();
    capturePostHogEvent("etf_screener_filters_cleared");
  }, []);

  const handleShare = useCallback(async () => {
    try {
      const url = new URL(window.location.href);
      url.search = etfStateToSearchParams({ filters: appliedFilters, sort, page }).toString();
      await copyTextToClipboard(url.toString());
      capturePostHogEvent("etf_screener_share_clicked", { page });
      message.success(t(`${p}filters.shareCopied`));
    } catch {
      message.error(t(`${p}filters.shareFailed`));
    }
  }, [appliedFilters, message, p, page, sort, t]);

  return (
    <div className={styles.screenerPanel}>
      <EtfScreenerFilters
        values={draftFilters}
        onChange={setDraftFilters}
        onApply={handleApply}
        onClear={handleClear}
        onShare={handleShare}
        isApplying={screener.isFetching}
      />

      <div
        className={`${styles.resultsSection} ${
          totalPages > 1 ? styles.resultsSectionWithPagination : ""
        }`}
      >
        <div className={styles.tableToolbar}>
          <div className={styles.toolbarLeft}>
            {screener.data && !screener.error && (
              <>
                <span className={styles.toolbarPage}>
                  {t(`${p}toolbar.page`, { current: page, total: totalPages })}
                </span>
                <span className={styles.toolbarMeta}>
                  {t(`${p}resultsCount`, { count: total })}
                </span>
              </>
            )}
          </div>
          <div className={styles.viewTabs} role="tablist">
            {ETF_VIEW_TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={viewTab === tab}
                className={`${styles.viewTab} ${viewTab === tab ? styles.viewTabActive : ""}`}
                onClick={() => setViewTab(tab)}
              >
                {t(`${p}viewTabs.${tab}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Raw API messages are never shown to users. */}
        {screener.error && (
          <div className={styles.errorBanner}>
            <Alert
              type="error"
              showIcon
              message={t(`${p}errors.loadFailed`)}
              action={
                <button type="button" onClick={() => void screener.refetch()}>
                  {t(`${p}errors.retry`)}
                </button>
              }
            />
          </div>
        )}

        <div className={`${styles.tableWrap} ${styles.denseTable}`}>
          <Table<EtfRow>
            rowKey="ticker"
            loading={screener.isLoading}
            columns={columns}
            dataSource={rows}
            pagination={false}
            size="small"
            bordered
            tableLayout="fixed"
            scroll={{ x: scrollX }}
            className={styles.denseTable}
            showSorterTooltip={false}
            onChange={handleTableChange}
            onRow={(row, index) => handleRow(row, index ?? 0)}
            locale={{ emptyText: t(`${p}empty`) }}
          />
        </div>
      </div>

      {totalPages > 1 && (
        <div className={styles.paginationWrap}>
          <CustomPagination
            current={page}
            pageSize={pageSize}
            total={total}
            onChange={(next) => setPage(Math.max(1, next))}
          />
        </div>
      )}
    </div>
  );
}
